import React from "react";
import { RotateCcw, Info } from "lucide-react";
import { EditField } from "../types";
import { ThemeFontValue, ThemeIconValue } from "../../../api/widgetThemeApi";
import colors from "../../../colors/colors";
import {
  TextField,
  OptionsField,
  ColorField,
  NumberField,
  ToggleField,
  FontField,
  IconField,
} from "../fields";

export interface FieldItemProps {
  field: EditField;
  values: Record<string, any>;
  onChange: (key: string, value: any) => void;
  savedKeys?: Set<string>;
  studioLockedKeys?: Set<string>;
  onResetField?: (key: string) => void;
  themeColors: Record<string, string>;
  themeFonts: Record<string, ThemeFontValue>;
  themeIcons: Record<string, ThemeIconValue>;
  openColorPicker: string | null;
  setOpenColorPicker: (key: string | null) => void;
}

function renderFieldControl(
  field: EditField,
  props: FieldItemProps,
): React.ReactNode {
  const value = props.values[field.key];

  switch (field.type) {
    case "text":
      return (
        <TextField
          value={value}
          onChange={(v) => props.onChange(field.key, v)}
          field={field}
        />
      );

    case "options":
      return (
        <OptionsField
          value={value}
          onChange={(v) => props.onChange(field.key, v)}
          field={field}
        />
      );

    case "color":
      return (
        <ColorField
          value={value}
          onChange={(v) => props.onChange(field.key, v)}
          field={field}
          themeColors={props.themeColors}
          openColorPicker={props.openColorPicker}
          setOpenColorPicker={props.setOpenColorPicker}
        />
      );

    case "number":
      return (
        <NumberField
          value={value}
          onChange={(v) => props.onChange(field.key, v)}
          field={field}
        />
      );

    case "toggle":
      return (
        <ToggleField
          value={value}
          onChange={(v) => props.onChange(field.key, v)}
          field={field}
        />
      );

    case "font":
      return (
        <FontField
          value={value}
          onChange={(v) => props.onChange(field.key, v)}
          field={field}
          themeFonts={props.themeFonts}
        />
      );

    case "icon":
      return (
        <IconField
          value={value}
          onChange={(v) => props.onChange(field.key, v)}
          field={field}
          themeIcons={props.themeIcons}
        />
      );

    default:
      return null;
  }
}

export const FieldItem: React.FC<FieldItemProps> = (props) => {
  const { field, savedKeys, studioLockedKeys, onResetField } = props;
  const isSaved = savedKeys?.has(field.key);
  const isLocked = studioLockedKeys?.has(field.key);

  return (
    <div
      style={{
        minWidth: 0,
        opacity: isLocked ? 0.5 : 1,
        pointerEvents: isLocked ? "none" : undefined,
      }}
    >
      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: "4px",
          fontSize: "11px",
          fontWeight: 600,
          color: colors.gray_medium,
          marginBottom: "8px",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
        }}
      >
        {field.label}
        {isLocked && (
          <span
            title="Edit this property from Concierge Studio"
            style={{
              display: "inline-flex",
              alignItems: "center",
              color: colors.blue,
              marginLeft: "2px",
              flexShrink: 0,
              cursor: "help",
              pointerEvents: "auto",
            }}
          >
            <Info size={10} />
          </span>
        )}
        {!isLocked && isSaved && onResetField && (
          <button
            onClick={() => onResetField(field.key)}
            title="Reset to default"
            className="edit-panel-reset-field"
            style={{
              background: "none",
              border: "none",
              padding: "1px",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              color: colors.gray_medium,
              opacity: 0.5,
              transition: "opacity 0.15s, color 0.15s",
              marginLeft: "2px",
              flexShrink: 0,
            }}
          >
            <RotateCcw size={10} />
          </button>
        )}
      </label>
      {renderFieldControl(field, props)}
    </div>
  );
};
